import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { handleZodError } from 'shared/hooks/utils';
import type { MetaData } from 'types/storeTypes';
import { z } from 'zod/v4';

// The generic view-rows endpoint returns a JSON:API list whose per-row shape
// varies by view (the server drives columns via `table_columns`). We validate
// only the envelope and flatten each row's `attributes` up — exactly what the
// redux-saga path did via `handlePageResponse(..., removeAttributes)`. This is
// the transitional bridge: per-resource hooks wrap it until the backend exposes
// dedicated resource list endpoints (as Tasks already has).
const ViewDataSchema = z.object({
  data: z.array(z.record(z.string(), z.unknown())),
  metadata: z.unknown(),
});

export type ViewDataParams = Record<string, unknown>;

// Hoist a row's `attributes` up so callers see a flat record (attributes win on
// conflicts). Exported so non-loader endpoints (e.g. Tasks) flatten the same way.
export const flattenJsonApiRow = ({ attributes, ...rest }: Record<string, unknown>) => ({
  ...rest,
  ...(attributes as Record<string, unknown> | undefined),
});

export async function loadViewDataQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  viewId: string,
  params: ViewDataParams,
) {
  const instance = await axiosInstance;
  // Relative to the legacy axios instance's `/private_api` baseURL (matches the
  // sibling option loaders, which pass bare paths like `recovery_steps`).
  const { data } = await instance.get(`views/${viewId}`, { params });

  const parseResult = ViewDataSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'view data');
    throw new SilentError();
  }

  return {
    data: parseResult.data.data.map(flattenJsonApiRow),
    // Validated permissively above; typed here as the contract consumers read
    // (metadata.pagination) — the envelope check guarantees the key is present
    // when the server sends it, and consumers optional-chain regardless.
    metadata: parseResult.data.metadata as MetaData | undefined,
  };
}
